import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Play } from 'lucide-react';
import { localizedPath } from '../utils/routes';
import { usePageTransition } from './PageTransition';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VideoCard = ({ video, index }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <motion.div
      className="flex-shrink-0 w-[85vw] md:w-[45vw] lg:w-[35vw] group cursor-pointer"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: index * 0.15 }}
      onClick={togglePlay}
      data-testid={`ai-video-card-${video.id}`}
    >
      <div className="relative aspect-video bg-black/50 rounded-xl overflow-hidden border border-white/10 group-hover:border-[#FFD700]/30 transition-all duration-500">
        <video
          ref={videoRef}
          src={`${API_URL}/api/videos/${video.id}/file`}
          className="w-full h-full object-cover"
          loop
          muted
          playsInline
          preload="metadata"
        />
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/20 transition-all duration-500">
            <motion.div
              className="w-16 h-16 rounded-full border-2 border-[#FFD700] flex items-center justify-center bg-black/60 backdrop-blur-sm"
              whileHover={{ scale: 1.1 }}
            >
              <Play className="w-6 h-6 text-[#FFD700] ml-1" />
            </motion.div>
          </div>
        )}
      </div>
      <div className="mt-4 px-1">
        <span className="text-[#FFD700] text-[10px] tracking-[0.3em] uppercase">{video.category}</span>
        <h4 className="font-display text-xl text-white mt-1 group-hover:text-[#FFD700] transition-colors duration-400">{video.title}</h4>
        {video.description && (
          <p className="text-white/40 text-sm mt-1 line-clamp-2">{video.description}</p>
        )}
      </div>
    </motion.div>
  );
};

const AIVideoSection = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || 'it';
  const { transitionTo } = usePageTransition();
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/videos?limit=4`)
      .then(res => res.json())
      .then(data => setVideos(data))
      .catch(() => {});
  }, []);

  return (
    <section className="py-32 bg-[#0a0a0a] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FFD700]/30 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-6 block">
              {t('ai_video.section_subtitle')}
            </span>
            <h2 className="font-display text-6xl md:text-7xl lg:text-8xl text-white leading-[0.9]">
              {t('ai_video.section_title')}{' '}
              <span className="text-outline">{t('ai_video.section_title_outline')}</span>
            </h2>
          </motion.div>

          <motion.p
            className="text-white/70 text-lg font-light max-w-md"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('ai_video.section_description')}
          </motion.p>
        </div>
      </div>

      {/* Video showcase */}
      {videos.length > 0 ? (
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-6 md:gap-8 pl-6 md:pl-12 lg:pl-16 pr-6 md:pr-12">
            {videos.map((video, index) => (
              <VideoCard key={video.id} video={video} index={index} />
            ))}
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            className="border border-dashed border-white/10 rounded-xl py-20 flex flex-col items-center gap-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 rounded-full border border-[#FFD700]/30 flex items-center justify-center">
              <Play className="w-7 h-7 text-[#FFD700]/50" />
            </div>
            <p className="text-white/30 text-sm tracking-wider uppercase">{t('ai_video.no_videos')}</p>
          </motion.div>
        </div>
      )}

      {/* CTA */}
      <motion.div
        className="flex flex-col items-center gap-4 mt-14"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <motion.button
          onClick={() => transitionTo(localizedPath(currentLang, 'ai-video'))}
          className="group flex items-center gap-3 px-8 py-3 border border-white/10 hover:border-[#FFD700]/40 transition-all duration-500"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          data-testid="ai-video-cta-btn"
        >
          <span className="text-white/50 group-hover:text-[#FFD700] text-xs tracking-[0.25em] uppercase transition-colors duration-500">
            {t('ai_video.cta')}
          </span>
          <svg className="w-4 h-4 text-white/30 group-hover:text-[#FFD700] group-hover:translate-x-1 transition-all duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </motion.button>
      </motion.div>
    </section>
  );
};

export default AIVideoSection;
